import { type FC, forwardRef, type ReactNode } from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../core';
import type { IconName, FAIconProps } from '../Icons/Icons';
import { FAIcon, IconStyle } from '../Icons/Icons';
import { Theme } from '../types/enums';
import { NextgenImage } from '../NextgenImage';
import RawTextLink from '../TextLink/RawTextLink';
import { TextLinkTheme, TextLinkUnderlineStyle } from '../TextLink/TextLink';
import { filterIconTheme } from '../utils/ThemeFilter/ThemeFilter';
import type {
  AlertMessagePropsTypes,
  AlertMessageTheme,
  ContentType,
  GetChevronIconPropTypes,
} from './types/propsType';
import { IconAlignment, TextWeight, AlertMessageWidth, AlertMessageType } from './types/enums';
import styles from './AlertMessage.module.scss';

const cx = classNames.bind(styles);

function getThemeIcon(type: Theme, icon?: IconName) {
  if (icon) {
    return icon;
  }

  const typeToIcon: { [key: string]: IconName } = {
    [Theme.SUCCESS]: 'circle-check',
    [Theme.WARNING]: 'triangle-exclamation',
    [Theme.ERROR]: 'square-exclamation',
    [Theme.ACTION]: 'bolt',
    [Theme.GRAY]: 'bolt',
    [Theme.BLUE]: 'calendar-check',
  };

  return typeToIcon[type] || null;
}
function themeFilter(theme?: Theme) {
  if (theme === Theme.GRAY || theme === Theme.WHITE || theme === Theme.PINK) {
    return Theme.BLACK;
  } else if (theme === Theme.ACTION) {
    return Theme.PURPLE;
  } else if (theme === Theme.RED || theme === Theme.ORANGE || theme === Theme.GREEN || theme === Theme.PURPLE) {
    return Theme.WHITE;
  }
  return theme;
}

const getIconStyle = (iconStyle: IconStyle | undefined, theme: AlertMessageTheme) => {
  if (iconStyle) {
    return iconStyle;
  } else if (theme === Theme.ACTION || theme === Theme.GRAY) {
    return IconStyle.SOLID;
  }
  return IconStyle.REGULAR;
};

const getHeadingElement = (content: ContentType, textColor: string) => {
  if (!content?.heading) {
    return null;
  }

  return (
    <span
      className={styles['alert-message-body-heading']}
      data-testid="alert-message-heading-test-id"
      {...(textColor ? { style: { color: textColor } } : {})}
    >
      {content.heading}
    </span>
  );
};

const getDetailElement = (content: ContentType, textColor: string) => {
  if (!content?.detail) {
    return null;
  }
  return (
    <>
      <br />
      <span className={styles['alert-message-body-detail']} {...(textColor ? { style: { color: textColor } } : {})}>
        {content.detail}
      </span>
    </>
  );
};

const getContentElement = (content: ContentType | undefined, children: ReactNode, textColor: string) => {
  if (!content) {
    return children;
  }
  return (
    <>
      {getHeadingElement(content, textColor)}
      {getDetailElement(content, textColor)}
    </>
  );
};

const getTextLinkOrChevron = ({
  textLink,
  onClose,
  onClick,
  onChevronClick,
  hideChevronIcon,
  theme,
}: GetChevronIconPropTypes) => {
  if (textLink?.text) {
    return (
      <RawTextLink
        href={textLink.href}
        text={textLink.text}
        onClick={textLink.onClick}
        underline={TextLinkUnderlineStyle.FIXED}
        className={styles['alert-message-text-link']}
        data-testid="alert-message-text-link-test-id"
        {...optionalProp('theme', themeFilter(theme) as TextLinkTheme)}
      />
    );
  } else if (!onClose && (onClick || onChevronClick) && !hideChevronIcon) {
    return (
      <FAIcon
        iconName="chevron-right"
        className={styles['icon']}
        data-testid="alert-message-action-test-icon"
        enableHover={false}
      />
    );
  }
  return null;
};

const AlertMessage: FC<AlertMessagePropsTypes> = forwardRef<HTMLButtonElement, AlertMessagePropsTypes>(
  (
    {
      children,
      content,
      iconColor,
      hasIcon = true,
      theme = Theme.SUCCESS,
      onChevronClick,
      onClick,
      onClose,
      className = '',
      textLink,
      icon,
      iconStyle,
      iconAlignment = IconAlignment.DEFAULT,
      bgColor = '',
      textColor = '',
      width,
      type = AlertMessageType.DEFAULT,
      hideChevronIcon = false,
      customIcon,
    }: AlertMessagePropsTypes,
    ref,
  ) => {
    const getNotificationIcon = () => icon ?? getThemeIcon(theme, icon);
    const getNotificationCustomImage = () => {
      if (customIcon) {
        return (
          <NextgenImage
            src={customIcon.src}
            alt={customIcon.alt}
            className={styles['primary-custom-icon']}
            data-testid="alert-message-custom-icon"
          />
        );
      } else if (hasIcon && getNotificationIcon()) {
        return (
          <FAIcon
            iconStyle={getIconStyle(iconStyle, theme)}
            className={styles['primary-icon']}
            enableHover={false}
            {...optionalProp('theme', filterIconTheme(iconColor || theme))}
            {...optionalProp('iconName', getNotificationIcon() as FAIconProps['iconName'])}
          />
        );
      }
      return null;
    };
    const getComponentWidth = () =>
      width || (textLink?.text ? AlertMessageWidth.FIT_CONTENT : AlertMessageWidth.FULL_WIDTH);

    const CClassname = cx('alert-message', [type, theme, getComponentWidth()], className);
    const CBodyClassname = cx('alert-message-main', [iconAlignment]);

    return (
      <button
        className={CClassname}
        type="button"
        data-testid="alert-message-test-id"
        ref={ref}
        onClick={onClick}
        {...(bgColor ? { style: { backgroundColor: bgColor, color: textColor } } : {})}
      >
        <div className={CBodyClassname}>
          {getNotificationCustomImage()}
          <div className={styles['alert-message-body']} data-testid="alert-message-body-test-id">
            {getContentElement(content, children, textColor)}
          </div>
        </div>
        <span className={styles['alert-message-action']}>
          {getTextLinkOrChevron({
            ...optionalProp('textLink', textLink),
            ...optionalProp('onClose', onClose),
            ...optionalProp('onClick', onClick),
            ...optionalProp('onChevronClick', onChevronClick),
            hideChevronIcon,
            theme,
          })}
          {onClose && (
            <FAIcon
              onClick={(e) => {
                e.stopPropagation();
                onClose(e);
              }}
              iconName="xmark"
              className={`${styles['icon']} ${styles['close-icon']}`}
              data-testid="alert-message-action-test-close-icon"
            />
          )}
        </span>
      </button>
    );
  },
);
AlertMessage.displayName = 'AlertMessage';
export { TextWeight, IconStyle, IconAlignment, AlertMessageWidth, AlertMessageType };
export default AlertMessage;
export type { AlertMessageTheme, AlertMessagePropsTypes };
